// AS DUAS GRANDEZAS DO CONTRATO, com nome próprio (feature 012).
//
// Até 11/09/2026 a coluna "Valor" mostrava o principal no contrato da Atlas e
// a soma das parcelas no legado — total a pagar com juros na mesma coluna que
// o valor solicitado. O cliente leu e concluiu o óbvio: não dá para comparar.
//
//   <ValorSolicitado>  o principal. Quando a Atlas não o conhece (legado sem
//                      informe) mostra "—" — NUNCA R$ 0,00 e nunca a soma.
//                      Depois do informe, o número com o selo "informado pelo
//                      banco".
//   <TotalEmFolha>     parcela × total de parcelas. Sempre conhecido, sempre
//                      sob este rótulo.
//
// O selo "legado" NÃO mora aqui (pedido do Caio, 11/09/2026): dentro da célula
// de valor ele lia como se "legado" fosse o valor, e a origem já tem lugar
// próprio — a coluna Origem na averbadora e na ADF, o selo ao lado do status
// nos cards do servidor, o botão "Informar dados" na carteira do banco.
//
// As três telas (averbadora, banco, servidor) usam estes dois; a regra de qual
// número vale mora em `@atlas/types/valor-contrato`, não aqui.

import { totalEmFolha, valorSolicitado, type ValoresDoContrato } from "@atlas/types";
import {
  seloInformadoPor,
  rotuloInformadoPor,
  TITULO_LEGADO_SEM_DADOS,
  type DadosInformadosDoContrato,
} from "../lib/dados-informados";

type ContratoValorado = ValoresDoContrato & {
  origem?: "atlas" | "legado";
  dadosInformados?: DadosInformadosDoContrato | null;
};

const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

export function ValorSolicitado({
  contrato,
  formatar = (n) => BRL.format(n),
  destaque,
}: {
  contrato: ContratoValorado;
  formatar?: (n: number) => string;
  /** Cor de destaque no número (o KV "accent" do servidor). */
  destaque?: boolean;
}) {
  const v = valorSolicitado(contrato);
  if (v == null) {
    const legado = contrato.origem === "legado";
    return (
      <span
        // Contrato da Atlas com principal zero e' dado furado, nao legado — o
        // tooltip nao pode explicar um com a historia do outro.
        title={legado ? TITULO_LEGADO_SEM_DADOS : "Valor solicitado não informado neste contrato."}
        style={{ whiteSpace: "nowrap", color: "var(--text-dim)" }}
      >
        —
      </span>
    );
  }
  const informe = contrato.dadosInformados ?? null;
  return (
    <span
      style={{
        whiteSpace: "nowrap",
        color: destaque ? "var(--accent)" : undefined,
        fontWeight: destaque ? 700 : undefined,
      }}
      title={informe ? rotuloInformadoPor(informe) : "Valor solicitado informado pela instituição na contratação."}
    >
      {formatar(v)}
      {informe ? (
        <span style={{ color: "var(--text-dim)", fontSize: 11, marginLeft: 5, fontWeight: 400 }}>
          {seloInformadoPor(informe)}
        </span>
      ) : null}
    </span>
  );
}

export function TotalEmFolha({
  contrato,
  formatar = (n) => BRL.format(n),
}: {
  contrato: Pick<ValoresDoContrato, "valorParcela" | "totalParcelas">;
  formatar?: (n: number) => string;
}) {
  if (!(contrato.totalParcelas > 0)) return <span title="Cronograma pendente: total em folha ainda não informado.">Não informado</span>;
  return (
    <span
      style={{ whiteSpace: "nowrap" }}
      title={`${contrato.totalParcelas} × ${formatar(contrato.valorParcela)}. É o que sai da folha ao longo do contrato — não é o valor solicitado.`}
    >
      {formatar(totalEmFolha(contrato))}
      <span style={{ color: "var(--text-dim)", fontSize: 11, marginLeft: 5 }}>{contrato.totalParcelas}×</span>
    </span>
  );
}
